import { useState, useRef, useEffect } from "react";
import { mockAppointments, getDayStatus } from "../../../mocks/appointments";

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

const typeOptions = [
  { value: "Yazılı", label: "Yazılı", icon: "ri-file-text-line", color: "#fdba74" },
  { value: "Sesli", label: "Sesli", icon: "ri-mic-line", color: "#d8b4fe" },
  { value: "Günlük", label: "Günlük (Horary)", icon: "ri-sun-line", color: "#fde68a" },
];

const serviceOptions = [
  "Doğum Haritası", "Sinastri", "Solar Return", "İlişki Analizi",
  "Geçmiş Yaşam ve Karmalar", "Yıllık Öngörü", "Rektifikasyon",
  "Eleksiyon", "Astrocartography", "Horary",
];

function CustomSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; icon: string; color: string }[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) || options[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-white cursor-pointer"
        style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.18)" }}
      >
        <span className="flex items-center gap-2">
          <i className={`${selected.icon} text-sm`} style={{ color: selected.color }} />
          <span>{selected.label}</span>
        </span>
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line text-sm`} style={{ color: "rgba(255,255,255,0.5)" }} />
      </button>
      {open && (
        <div
          className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden"
          style={{ background: "#2a2260", border: "1px solid rgba(168,85,247,0.4)" }}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm cursor-pointer transition-colors text-left whitespace-nowrap"
              style={{
                color: value === opt.value ? opt.color : "rgba(255,255,255,0.85)",
                background: value === opt.value ? "rgba(168,85,247,0.2)" : "transparent",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = value === opt.value ? "rgba(168,85,247,0.2)" : "transparent")}
            >
              <i className={`${opt.icon} text-sm`} style={{ color: opt.color }} />
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm cursor-pointer"
        style={{
          background: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.18)",
          color: value ? "#fff" : "rgba(255,255,255,0.4)",
        }}
      >
        <span>{value || "Hizmet seç..."}</span>
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line text-sm`} style={{ color: "rgba(255,255,255,0.5)" }} />
      </button>
      {open && (
        <div
          className="absolute z-50 w-full mt-1 rounded-xl overflow-auto max-h-48"
          style={{ background: "#2a2260", border: "1px solid rgba(168,85,247,0.4)" }}
        >
          {serviceOptions.map((svc) => (
            <button
              key={svc}
              type="button"
              onClick={() => { onChange(svc); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 text-sm cursor-pointer transition-colors whitespace-nowrap"
              style={{ color: value === svc ? "#d8b4fe" : "rgba(255,255,255,0.85)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {svc}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

function pad(n: number) {
  return n.toString().padStart(2, "0");
}

const typeColors: Record<string, string> = {
  Sesli: "#a855f7",
  Yazılı: "#f97316",
  Günlük: "#d4af37",
};

export default function CalendarSystem() {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(2); // March = 2
  const [selectedDate, setSelectedDate] = useState<string | null>("2026-03-24");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newApp, setNewApp] = useState({ clientName: "", service: "", type: "Yazılı", time: "" });

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const calendarCells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const selectedApps = selectedDate
    ? mockAppointments.filter((a) => a.date === selectedDate)
    : [];

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const getDayApps = (day: number) => {
    const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
    return mockAppointments.filter((a) => a.date === dateStr);
  };

  const getStatusColor = (dateStr: string) => {
    const status = getDayStatus(dateStr);
    if (status === "full") return "#ef4444";
    if (status === "filling") return "#f59e0b";
    return "#10b981";
  };

  const isWeekend = (day: number) => {
    const d = new Date(year, month, day).getDay();
    return d === 0 || d === 6;
  };

  const isSaturday = (day: number) => new Date(year, month, day).getDay() === 6;
  const isSunday = (day: number) => new Date(year, month, day).getDay() === 0;

  const getRules = () => [
    { icon: "ri-mic-line", color: "#a855f7", text: "Sesli: Sadece Cmt & Paz" },
    { icon: "ri-file-text-line", color: "#f97316", text: "Yazılı: Hafta içi max 2, hafta sonu max 5" },
    { icon: "ri-sun-line", color: "#d4af37", text: "Horary: Her gün 20:00–24:00" },
    { icon: "ri-refresh-line", color: "#06b6d4", text: "Rektifikasyon: +4 ile +7 gün teslim" },
  ];

  return (
    <div className="p-6 space-y-5 overflow-y-auto h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            ✦ Akıllı Randevu Takvimi
          </h1>
          <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.4)" }}>
            Otomatik kapasite kontrolü ile akıllı planlama
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
          style={{ background: "linear-gradient(135deg, #a855f7, #7c3aed)", color: "#fff" }}
        >
          <i className="ri-calendar-add-line" />
          Randevu Ekle
        </button>
      </div>

      {/* Rules */}
      <div className="grid grid-cols-4 gap-3">
        {getRules().map((r) => (
          <div key={r.text} className="flex items-center gap-3 px-4 py-3 rounded-xl"
            style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)" }}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: `${r.color}28` }}>
              <i className={`${r.icon} text-sm`} style={{ color: r.color }} />
            </div>
            <p className="text-xs leading-snug font-medium" style={{ color: "rgba(255,255,255,0.75)" }}>{r.text}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-5">
        {/* Calendar */}
        <div className="col-span-2 rounded-2xl overflow-hidden"
          style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)" }}>
          {/* Month Nav */}
          <div className="flex items-center justify-between px-5 py-4"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
            <button onClick={prevMonth}
              className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all hover:bg-white/10"
              style={{ color: "rgba(255,255,255,0.7)" }}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-s-line text-lg" />
              </div>
            </button>
            <h2 className="font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              {MONTHS[month]} {year}
            </h2>
            <button onClick={nextMonth}
              className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all hover:bg-white/10"
              style={{ color: "rgba(255,255,255,0.7)" }}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-lg" />
              </div>
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 px-4 pt-3">
            {DAYS.map((d, i) => (
              <div key={d} className="text-center text-xs font-semibold pb-2"
                style={{ color: i >= 5 ? "#fde68a" : "rgba(255,255,255,0.6)" }}>
                {d}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 px-4 pb-4">
            {calendarCells.map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} />;
              const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
              const apps = getDayApps(day);
              const isSelected = selectedDate === dateStr;
              const statusColor = apps.length > 0 ? getStatusColor(dateStr) : null;
              const isToday = dateStr === "2026-03-24";
              const weekend = isWeekend(day);

              return (
                <button
                  key={day}
                  onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                  className="relative aspect-square flex flex-col items-center justify-center rounded-xl cursor-pointer transition-all hover:bg-white/[0.1] p-1"
                  style={
                    isSelected
                      ? { background: "rgba(168,85,247,0.4)", border: "1px solid rgba(168,85,247,0.7)" }
                      : isToday
                        ? { background: "rgba(212,175,55,0.25)", border: "1px solid rgba(212,175,55,0.55)" }
                        : { border: "1px solid transparent" }
                  }
                >
                  <span className="text-sm leading-none"
                    style={{
                      color: isSelected ? "#fff" : isToday ? "#fde68a" : weekend ? "rgba(253,230,138,0.85)" : "rgba(255,255,255,0.85)",
                      fontWeight: isToday || isSelected ? 600 : 400,
                    }}>
                    {day}
                  </span>
                  {apps.length > 0 && (
                    <div className="flex gap-0.5 mt-1 flex-wrap justify-center max-w-full">
                      {apps.slice(0, 3).map((a, i) => (
                        <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: typeColors[a.type] || "#fff" }} />
                      ))}
                    </div>
                  )}
                  {statusColor && (
                    <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="px-5 py-3 flex items-center gap-5" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            {[
              { color: "#10b981", label: "Müsait" },
              { color: "#f59e0b", label: "Dolmak üzere" },
              { color: "#ef4444", label: "Dolu" },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.65)" }}>{l.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: "#a855f7" }} />
              <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.65)" }}>Sesli</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: "#f97316" }} />
              <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.65)" }}>Yazılı</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: "#d4af37" }} />
              <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.65)" }}>Horary</span>
            </div>
          </div>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Selected Day Details */}
          <div className="rounded-2xl p-4"
            style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)" }}>
            <h3 className="font-semibold text-white text-sm mb-3 flex items-center gap-2"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              <i className="ri-calendar-event-line text-sm" style={{ color: "#d4af37" }} />
              {selectedDate ? selectedDate : "Gün Seçin"}
            </h3>
            {selectedDate ? (
              <div className="space-y-2">
                {selectedApps.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="ri-calendar-2-line text-2xl" style={{ color: "rgba(255,255,255,0.3)" }} />
                    <p className="text-xs mt-2" style={{ color: "rgba(255,255,255,0.55)" }}>
                      Bu gün boş ✦
                    </p>
                    <p className="text-xs mt-1 font-medium" style={{ color: "#34d399" }}>
                      {isWeekend(new Date(selectedDate).getDate()) ? "Maks. 5 randevu" : "Maks. 2 yazılı"}
                    </p>
                  </div>
                ) : (
                  selectedApps.map((app) => (
                    <div key={app.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                      style={{ background: "rgba(255,255,255,0.07)" }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold"
                        style={{ background: `${typeColors[app.type]}35`, color: typeColors[app.type] }}>
                        <i className={app.type === "Sesli" ? "ri-mic-fill" : app.type === "Günlük" ? "ri-sun-fill" : "ri-file-text-fill"} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{app.clientName}</p>
                        <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.6)" }}>
                          {app.service}{app.time ? ` · ${app.time}` : ""}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <p className="text-xs text-center py-4" style={{ color: "rgba(255,255,255,0.5)" }}>
                Takvimden bir gün seçin
              </p>
            )}
          </div>

          {/* Day Rules Info */}
          {selectedDate && (
            <div className="rounded-2xl p-4"
              style={{
                background: "rgba(212,175,55,0.1)",
                border: "1px solid rgba(212,175,55,0.3)",
              }}>
              <h3 className="text-xs font-semibold mb-2" style={{ color: "#fde68a" }}>
                <i className="ri-information-line mr-1" />
                Gün Kuralları
              </h3>
              {(() => {
                const d = new Date(selectedDate);
                const dow = d.getDay();
                const sat = dow === 6;
                const sun = dow === 0;
                const weekend = sat || sun;
                return (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.75)" }}>
                      <i className={weekend ? "ri-check-line text-green-400" : "ri-close-line text-red-400"} />
                      {" "}Sesli danışmanlık: {weekend ? "✓ Uygun" : "✗ Yalnızca Cmt/Paz"}
                    </p>
                    <p className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.75)" }}>
                      <i className="ri-check-line text-green-400" />
                      {" "}Yazılı danışmanlık: ✓ Max {weekend ? "5" : "2"}
                    </p>
                    <p className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.75)" }}>
                      <i className="ri-check-line text-green-400" />
                      {" "}Horary: ✓ 20:00–24:00
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Add Form */}
          {showAddForm && (
            <div className="rounded-2xl p-4"
              style={{ background: "rgba(168,85,247,0.12)", border: "1px solid rgba(168,85,247,0.35)" }}>
              <h3 className="text-sm font-semibold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Yeni Randevu
              </h3>
              <div className="space-y-2">
                <input
                  placeholder="Müşteri adı"
                  value={newApp.clientName}
                  onChange={(e) => setNewApp({ ...newApp, clientName: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none placeholder:text-white/40"
                  style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.18)" }}
                />
                <ServiceSelect
                  value={newApp.service}
                  onChange={(v) => setNewApp({ ...newApp, service: v })}
                />
                <CustomSelect
                  value={newApp.type}
                  onChange={(v) => setNewApp({ ...newApp, type: v })}
                  options={typeOptions}
                />
                <button
                  className="w-full py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap"
                  style={{ background: "linear-gradient(135deg, #a855f7, #7c3aed)", color: "#fff" }}>
                  Randevu Kaydet
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
